import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import Page from "@/components/Page";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import SwitchView from "@/components/SwitchView";
import StatsContainer from "@/components/StatsContainer";
import {PlayerAPI} from "@/api/player";

import {dateFormat, rpeOptions, ERROR_OPERATION} from "@/utils/constants";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const fieldDateFormat = "YYYY-MM-DD";

function RPEPage(props) {
	const {handleNotification} = props;

	// const seasonId = useSelector((state) => state?.season?.id);
	const userRole = useSelector((state) => state?.user?.roleName);
	const playerId = useSelector((state) => state?.user?.playerId);
	const isPlayer = userRole === "Player";

	const [currentSwitchView, setCurrentSwitchView] = useState("list");

	const defaultFiltersData = {
		fromDate: moment()
			.startOf(isPlayer ? "month" : "day")
			.format(fieldDateFormat),
		toDate: moment()
			.endOf(isPlayer ? "month" : "day")
			.format(fieldDateFormat),
	};

	const [loading, setLoading] = useState(false);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
	});

	const [rpe, setRpe] = useState([]);
	const [filtersData, setFiltersData] = useState(
		JSON.parse(
			sessionStorage.getItem("rpe_filters" + isPlayer ? "_player" : ""),
		) || defaultFiltersData,
	);

	useEffect(() => {
		if (filtersData) {
			sessionStorage.setItem(
				"rpe_filters" + isPlayer ? "_player" : "",
				JSON.stringify(filtersData),
			);
		}
	}, [filtersData]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: moment(filtersData?.fromDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					const filters = {
						...filtersData,
						fromDate: moment(e).format(fieldDateFormat),
					};
					setFiltersData(filters);
				}
			},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: moment(filtersData?.toDate).format(fieldDateFormat),
			setValue: (e) => {
				if (e) {
					const filters = {
						...filtersData,
						toDate: moment(e).format(fieldDateFormat),
					};
					setFiltersData(filters);
				}
			},
		},
		// {
		// 	key: "categoryId",
		// 	type: "select",
		// 	options: categories?.map((c) => ({
		// 		value: c.id,
		// 		label: c.name,
		// 	})),
		// 	label: "Categoria",
		// 	sorted: false,
		// 	customClassName: "col-12 col-sm-4",
		// 	clearable: true,
		// 	value: filtersData?.categoryId,
		// 	setValue: (value) => {
		// 		setFiltersData({...filtersData, categoryId: value});
		// 	},
		// },
	];

	const getRPE = async () => {
		setLoading(true);
		try {
			const apiFilters = {
				...filtersData,
				// categoryId: filtersData?.categoryId?.value,
				playerId,
				// seasonId,
			};
			const response = await PlayerAPI.listRPE(apiFilters);
			setRpe(
				response.map((e) => {
					return e;
				}),
			);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (currentSwitchView === "list") {
			getRPE();
		}
	}, [filtersData, currentSwitchView]);

	const columns = [
		{
			label: "Data",
			key: "date",
			customRender: (item) => {
				return moment(item.rpeDate).format(dateFormat);
			},
			small: true,
		},
	];

	if (!isPlayer) {
		columns.push({
			label: "Giocatore",
			key: "playerId",
			customRender: (item) =>
				item.player
					? item.player.firstName + " " + item.player.lastName
					: "---",
			align: "start",
		});
	}

	columns.push({
		label: "RPE",
		key: "rpe",
		customRender: (item) => {
			return !item.emptyRow
				? rpeOptions.filter((o) => o.value === item.rpe)?.[0]?.label
				: "Nessun dato inserito";
		},
		align: "start",
	});

	const mainActions = [];

	const moveActions = [
		{
			variant: "outlined",
			label: isPlayer ? "<< mese" : "<< giorno",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.subtract(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
					toDate: moment(filtersData?.toDate)
						.subtract(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
				});
			},
		},
		{
			variant: "outlined",
			label: isPlayer ? "mese >>" : "giorno >>",
			onClick: () => {
				setFiltersData({
					...filtersData,
					fromDate: moment(filtersData?.fromDate)
						.add(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
					toDate: moment(filtersData?.toDate)
						.add(1, isPlayer ? "months" : "days")
						.format(fieldDateFormat),
				});
			},
		},
	];

	const rpeData = [...rpe];

	// const groupedRPE = {};
	// categories
	// 	.filter((c) => c.name !== "Prima Squadra" && c.name !== "Non categorizzato")
	// 	.forEach((c) => {
	// 		const currentCategoryName = c.name;
	// 		const rpeItems = rpeData?.filter((t) => {
	// 			return t.category?.name === currentCategoryName;
	// 		});
	// 		groupedRPE[currentCategoryName] = rpeItems;
	// 	});

	const listConfiguration = {
		// data: isPlayer ? rpeData : groupedRPE,
		data: rpeData,
	};

	return (
		<Page className="rpe-container">
			<Loading visible={loading} />
			{!isPlayer && (
				<SwitchView
					currentSwitchView={currentSwitchView}
					setCurrentSwitchView={setCurrentSwitchView}
					pageOptions={[
						{value: "list", label: "Dati temporali"},
						{value: "stats", label: "Overview"},
					]}
				/>
			)}
			<Breadcrumbs
				actions={
					<MainActions
						actions={
							isPlayer
								? [...moveActions, ...mainActions]
								: currentSwitchView === "list"
									? moveActions
									: []
						}
					/>
				}
			/>
			{currentSwitchView === "list" ? (
				<CardsContainer
					tableComponent={
						<CardsTable
							filtersData={filters}
							configurationColumns={columns}
							data={rpeData}
							defaultSortedField="rpeDate"
							defaultSortedOrder="descend"
						/>
					}
					listComponent={
						<CardsList
							type="rpe"
							breadcrumbs
							// grouped={!isPlayer}
							filtersData={filters}
							listConfiguration={listConfiguration}
							// groupSorted={false}
							defaultSortedField="rpeDate"
							defaultSortedOrder="descend"
						/>
					}
				/>
			) : (
				<StatsContainer type="rpe" categories={categories} />
			)}
		</Page>
	);
}

export default RPEPage;
